import { CancelToken, client, isCancel } from "./client";
import { Canceler } from "axios";
import { AuthenticatedUser } from "./services";

export const getCallbackPath = () => "/oauth2callback";
export const getLogoutPath = () => "/logout";

export const redirectUri = new URL(getCallbackPath(), window.location.href)
  .href;
export const logoutUri = new URL(getLogoutPath(), window.location.href).href;

export type AuthSession = {
  sessionId: string | null;
  sessionInitialPath: string | null;
};

export type AuthConfig = {
  clientId: string;
  logoutUrl: string;
  authorizeUrl: string;
  callbackPath: string;
  logoutPath: string;
};

const authConfigFields = [
  "clientId",
  "logoutUrl",
  "authorizeUrl",
  "callbackPath",
  "logoutPath",
]
  .sort()
  .join(",");

// Check if an arbitrary input data is of type AuthConfig
// There must be a better way to do this
export const verifyAuthConfig = (data: any): AuthConfig => {
  const fields = Object.keys(data).sort().join(",");
  if (authConfigFields !== fields) {
    throw new Error("Invalid authentication config payload");
  }
  return data as AuthConfig;
};

export const buildOAuth2State = ({
  sessionId,
  sessionInitialPath,
}: AuthSession) => {
  if (sessionId === null || sessionInitialPath === null) {
    throw new Error("Invalid session: id or path is empty");
  }
  return encodeURIComponent(
    btoa(JSON.stringify({ sessionId, sessionInitialPath })),
  );
};

const verifyOAuth2State = (
  state: string,
  { sessionId, sessionInitialPath }: AuthSession,
) => {
  const {
    sessionId: actualSessionId,
    sessionInitialPath: actualSessionInitialPath,
  } = JSON.parse(atob(decodeURIComponent(state)));
  if (
    actualSessionId !== sessionId ||
    actualSessionInitialPath !== sessionInitialPath
  ) {
    throw new Error("state mismatch");
  }
};

export const getOAuth2CodeUrl = (
  session: AuthSession,
  { clientId, authorizeUrl }: AuthConfig,
) => {
  const params = {
    response_type: "code",
    client_id: clientId,
    redirect_uri: redirectUri,
    state: buildOAuth2State(session),
  };
  return `${authorizeUrl}?${new URLSearchParams(params).toString()}`;
};

export const requestOAuth2Token = (
  code: string,
): [Promise<AuthenticatedUser | void>, Canceler] => {
  // Make and capture request promise
  const { token: cancelToken, cancel } = CancelToken.source();
  const promise = client
    .post<AuthenticatedUser>(
      "/oauth2client/token",
      {
        code,
        redirectUri,
      },
      {
        // Add cancel token to request
        cancelToken,
      },
    )
    .then<AuthenticatedUser>(({ data }) => {
      if (data) {
        if (data.name === undefined) {
          throw new Error("Unexpected token response");
        }
      }
      return data;
    })
    .catch((err) => {
      // handle errors
      if (!isCancel(err)) {
        throw new Error(err);
      }
    });

  // Return [Promise of token, cancel request fn] for consumer
  return [promise, cancel];
};

export const getValidOAuth2Code = (
  callbackUrl: string,
  authSession: AuthSession,
) => {
  const baseUrl = callbackUrl.split("?")[0];
  if (baseUrl !== redirectUri) {
    throw new Error("Invalid callback url");
  }
  const urlObj = new URL(callbackUrl);
  const error = urlObj.searchParams.get("error");
  if (error) {
    throw new Error(error);
  }
  const code = urlObj.searchParams.get("code");
  const state = urlObj.searchParams.get("state");
  if (!state) {
    throw new Error("Empty state");
  }
  if (!code) {
    throw new Error("Empty authorisation code");
  }
  try {
    verifyOAuth2State(state, authSession);
  } catch (e) {
    throw new Error("Invalid state: " + e.message);
  }
  return code;
};
